import { isApiError } from "@/api/_client";
import { requestVerificationEmail, useUser, verifyEmail } from "@/api/user";
import { Card } from "@/components/ui/card";
import { SimpleLink } from "@/components/ui/simple-link";
import { toast } from "@/components/ui/use-toast";
import { useAppContext } from "@/providers/app-context";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { Navigate, useParams } from "react-router-dom";

function VerifyEmail() {
  const { uid, token } = useParams();
  const { setShowAbsoluteSpinner, logOut, setHideSidebar } = useAppContext();
  const queryClient = useQueryClient();
  const currentUser = useUser();
  const [error, setError] = React.useState<string>();

  React.useEffect(() => {
    setHideSidebar(true);
    setShowAbsoluteSpinner(true);

    const verify = async (uid: string, token: string) => {
      const res = await verifyEmail(uid, token);
      if (isApiError(res)) {
        setError(
          "Failed to verify your email. Please double check your link or request a new verification email.",
        );
      } else {
        toast({
          title: "Email Verified",
          description: "Thank you for verifying your email.",
        });
        queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      }
      setShowAbsoluteSpinner(false);
    };
    if (uid && token) verify(uid, token);
    else setShowAbsoluteSpinner(false);

    return () => setHideSidebar(false);
  }, []);

  const [resendDisabled, setResendDisabled] = React.useState(false);

  if (currentUser.data?.email_verified) return <Navigate to="/" />;
  return (
    <div className="h-svh flex items-center justify-center">
      <Card className="flex max-w-sm flex-col gap-y-4">
        <div className="flex flex-col gap-y-4">
          {error ? (
            error
          ) : (
            <>
              <p>
                Welcome! We've sent an email with a verification link. Please
                check your inbox and click the link to verify your email.
              </p>
              <p>
                If it's not in your inbox, a quick check in your spam folder
                might do the trick.
              </p>
              <p>
                If you didn't receive the email,{" "}
                <SimpleLink
                  disabled={resendDisabled}
                  onClick={() => {
                    requestVerificationEmail();
                    toast({
                      title: "Verification Email Sent",
                      description:
                        "We've sent a new verification email to your inbox.",
                    });
                    setResendDisabled(true);
                    setTimeout(() => setResendDisabled(false), 60000);
                  }}
                >
                  click here
                </SimpleLink>{" "}
                to request a new one.
              </p>
              <div className="mt-4 w-full text-center">
                <SimpleLink onClick={() => logOut()}>Log out</SimpleLink>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

export default VerifyEmail;
